import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { HeaderLanguageEnum } from '@global/enums';
import { select, Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { combineLatest, Observable, of } from 'rxjs';
import { getGwCookie, getSessionId, getTkJwt } from '@store/flow-data/general';
import { switchMap, take } from 'rxjs/operators';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  protected readonly store: Store<AppState>;
  private readonly http: HttpClient;

  constructor(protected readonly injector: Injector) {
    this.store = this.injector.get(Store);
    this.http = this.injector.get(HttpClient);
  }

  private getHeaders({ headers, authenticationType }: HeadersPayload): Observable<HttpHeaders> {
    let defaultHeaders: HttpHeaders = new HttpHeaders({
      ...headers,
      Accept: 'application/json',
      'Content-type': 'application/json',
      [HeadersEnum.ACCEPT_LANGUAGE]: headers?.[HeadersEnum.ACCEPT_LANGUAGE]?.toString() ?? HeaderLanguageEnum.ITA,
    });

    return combineLatest([
      this.store.pipe(select(getSessionId)),
      this.store.pipe(select(getTkJwt)),
      this.store.pipe(select(getGwCookie)),
    ]).pipe(
      take(1),
      switchMap(([sessionId, jwt, gwCookie]) => {
        if (jwt && authenticationType === 'jwt') {
          defaultHeaders = defaultHeaders.append(
            HeadersEnum.AUTHORIZATION,
            `Bearer ${headers?.[HeadersEnum.AUTHORIZATION]?.toString() ?? jwt.toString()}`
          );
        }
        if (sessionId) {
          defaultHeaders = defaultHeaders.append(
            HeadersEnum.X_SESSION_ID,
            headers?.[HeadersEnum.X_SESSION_ID]?.toString() ?? sessionId.toString()
          );
        }
        if (gwCookie) {
          defaultHeaders = defaultHeaders.append(
            HeadersEnum.GWCOOKIE,
            headers?.[HeadersEnum.GWCOOKIE]?.toString() ?? gwCookie.toString()
          );
        }
        return of(defaultHeaders);
      })
    );
  }

  private toHttpParameters<T>(parameters: T): HttpOutputParametersType {
    return Object.entries(parameters || {}).reduce(
      (accumulator: HttpOutputParametersType, [key, value]: [string, T[keyof T]]) => {
        if (!value) {
          return accumulator;
        }
        accumulator[key] = Array.isArray(value)
          ? // tslint:disable-next-line: no-any
            value.filter(Boolean).map((v: any) => v.toString())
          : value.toString();
        return accumulator;
      },
      {} as HttpOutputParametersType
    );
  }

  /**
   * `get<ResponseModel>`
   */
  get<T>(data: ApiRequestPayload<void>): Observable<T>;
  get<T>(data: ApiRequestPayload<void>, requestType: 'response'): Observable<HttpResponse<T>>;

  get<T>(
    {
      apiUrl = environment.apiUrl,
      suffix,
      headers,
      authenticationType = 'basic',
      queryParams,
    }: ApiRequestPayload<void>,
    requestType: RequestType = 'body'
  ): Observable<T | HttpResponse<T>> {
    if (requestType === 'body') {
      return this.makeResponseRequest<void, T>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          queryParams,
        },
        HttpMethodEnum.GET
      );
    }
    if (requestType === 'response') {
      return this.makeHttpResponseRequest<void, T>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          queryParams,
        },
        HttpMethodEnum.GET
      );
    }
  }

  /**
   * `post<RequestModel,ResponseModel>`
   */
  post<T, U>(data: ApiRequestPayload<T>): Observable<U>;
  post<T, U>(data: ApiRequestPayload<T>, requestType: 'response'): Observable<HttpResponse<U>>;

  post<T, U>(
    {
      apiUrl = environment.apiUrl,
      suffix,
      headers,
      authenticationType = 'basic',
      queryParams,
      body,
    }: ApiRequestPayload<T>,
    requestType: RequestType = 'body'
  ): Observable<U | HttpResponse<U>> {
    if (requestType === 'body') {
      return this.makeResponseRequest<T, U>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          body,
          queryParams,
        },
        HttpMethodEnum.POST
      );
    }
    if (requestType === 'response') {
      return this.makeHttpResponseRequest<T, U>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          body,
          queryParams,
        },
        HttpMethodEnum.POST
      );
    }
  }

  /**
   * `put<RequestModel,ResponseModel>`
   */
  put<T, U>(data: ApiRequestPayload<T>): Observable<U>;
  put<T, U>(data: ApiRequestPayload<T>, requestType: 'response'): Observable<HttpResponse<U>>;

  put<T, U>(
    {
      apiUrl = environment.apiUrl,
      suffix,
      headers,
      authenticationType = 'basic',
      queryParams,
      body,
    }: ApiRequestPayload<T>,
    requestType: RequestType = 'body'
  ): Observable<U | HttpResponse<U>> {
    if (requestType === 'body') {
      return this.makeResponseRequest<T, U>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          body,
          queryParams,
        },
        HttpMethodEnum.PUT
      );
    }
    if (requestType === 'response') {
      return this.makeHttpResponseRequest<T, U>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          body,
          queryParams,
        },
        HttpMethodEnum.PUT
      );
    }
  }

  /**
   * `delete<ResponseModel>`
   */
  delete<T>(data: ApiRequestPayload<void>): Observable<T>;
  delete<T>(data: ApiRequestPayload<void>, requestType: 'response'): Observable<HttpResponse<T>>;

  delete<T>(
    {
      apiUrl = environment.apiUrl,
      suffix,
      headers,
      authenticationType = 'basic',
      queryParams,
    }: ApiRequestPayload<void>,
    requestType: RequestType = 'body'
  ): Observable<T | HttpResponse<T>> {
    if (requestType === 'body') {
      return this.makeResponseRequest<void, T>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          queryParams,
        },
        HttpMethodEnum.DELETE
      );
    }
    if (requestType === 'response') {
      return this.makeHttpResponseRequest<void, T>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          queryParams,
        },
        HttpMethodEnum.DELETE
      );
    }
  }

  private makeResponseRequest<T, U>(
    { apiUrl, suffix, headers, authenticationType, queryParams, body }: ApiRequestPayload<T>,
    method: HttpMethodEnum
  ): Observable<U> {
    return this.getHeaders({ headers, authenticationType }).pipe(
      switchMap((headers: HttpHeaders) => {
        if (method === HttpMethodEnum.GET) {
          return this.http.get<U>(`${apiUrl}${suffix}`, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'body',
          });
        }
        if (method === HttpMethodEnum.POST) {
          return this.http.post<U>(`${apiUrl}${suffix}`, body ?? {}, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'body',
          });
        }
        if (method === HttpMethodEnum.PUT) {
          return this.http.put<U>(`${apiUrl}${suffix}`, body ?? {}, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'body',
          });
        }
        if (method === HttpMethodEnum.DELETE) {
          return this.http.delete<U>(`${apiUrl}${suffix}`, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'body',
          });
        }
      })
    );
  }

  private makeHttpResponseRequest<T, U>(
    { apiUrl, suffix, headers, authenticationType, queryParams, body }: ApiRequestPayload<T>,
    method: HttpMethodEnum
  ): Observable<HttpResponse<U>> {
    return this.getHeaders({ headers, authenticationType }).pipe(
      switchMap((headers: HttpHeaders) => {
        if (method === HttpMethodEnum.GET) {
          return this.http.get<U>(`${apiUrl}${suffix}`, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'response',
          });
        }
        if (method === HttpMethodEnum.POST) {
          return this.http.post<U>(`${apiUrl}${suffix}`, body ?? {}, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'response',
          });
        }
        if (method === HttpMethodEnum.PUT) {
          return this.http.put<U>(`${apiUrl}${suffix}`, body ?? {}, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'response',
          });
        }
        if (method === HttpMethodEnum.DELETE) {
          return this.http.delete<U>(`${apiUrl}${suffix}`, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'response',
          });
        }
      })
    );
  }
}

interface ApiRequestPayload<U> extends HeadersPayload {
  apiUrl?: string;
  suffix: string;
  queryParams?: HttpInputParametersType;
  body?: U;
}

interface HeadersPayload {
  headers?: DefaultHttpHeaders;
  authenticationType?: AuthenticationType;
}

export interface DefaultHttpHeaders {
  [HeadersEnum.ACCEPT_LANGUAGE]?: AcceptLanguageEnum;
  [HeadersEnum.X_SESSION_ID]?: string;
  [HeadersEnum.GWCOOKIE]?: string;
}

type AcceptLanguageEnum = HeaderLanguageEnum.ITA;
type PrimitiveType = string | number | boolean;
type HttpInputParametersType = Record<string, PrimitiveType | PrimitiveType[]>;
type HttpOutputParametersType = Record<string, string | string[]>;
type AuthenticationType = 'basic' | 'jwt' | 'none';
type RequestType = 'body' | 'response';
enum RequestTypeEnum {
  BODY = 'body',
  RESPONSE = 'response',
}
enum HttpMethodEnum {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  DELETE = 'delete',
}
export enum HeadersEnum {
  ACCEPT_LANGUAGE = 'Accept-Language',
  X_SESSION_ID = 'X-Session-Id',
  AUTHORIZATION = 'Authorization',
  GWCOOKIE = 'GWCOOKIE',
}
